import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchRecentQuestions, fetchClinicalAreas, fetchQuestionTypes, formatDate, countQuestions } from '../../utils/asktrip';
import LoadingAnimation from '../../components/LoadingAnimation';
import { set } from 'store';

export function AskTripQuestions(props) {

    // Clinical areas from the backend
    const [areas, setAreas] = useState(null);

    // Currently selected clinical area
    const [area, setArea] = useState(null);

    // Question types from the backend
    const [types, setTypes] = useState(null);

    // Currently selected question type
    const [type, setType] = useState(null);

    // Quality of evidence options
    const [qualities] = useState(['High', 'Good', 'Limited', 'Moderate']);

    // Currently selected quality of evidence
    const [quality, setQuality] = useState(null);

    // Fetch clinical areas and question types from the backend on load
    useEffect(() => {
        fetchClinicalAreas().then((data) => {
            setAreas(data);
        });
        fetchQuestionTypes().then((data) => {
            setTypes(data);
        });
    }, []);

    // Get the search query from the URL parameters
    const searchParams = new URLSearchParams(window.location.search);

    // The currently active search query, defaulting to the query parameter or an empty string
    const [searchValue, _setSearchvalue] = useState(searchParams.get('q') || '');
    const setSearchvalue = (value) => {
        _setSearchvalue(value);
        window.history.replaceState({}, '', `/ask/search?q=${encodeURIComponent(value)}`);
        setArea(null); // Reset area filter when search changes
        setType(null); // Reset type filter when search changes
        setQuality(null); // Reset quality filter when search changes
    };

    // The new search query being input
    const [value, setValue] = useState(searchValue);

    const [count,setCount] = useState(null);

    const [questions, setQuestions] = useState(null);

    const [searching, setSearching] = useState(false);

    const [showMore, setShowMore] = useState(false);

    const [error, setError] = useState(null);

    useEffect(() => {
        
        setQuestions(null);
        setError(null);
        setCount(null);
        if (!props.searchEmpty && searchValue.trim().length===0) {
            // If no search value and no filters, do not fetch questions
            return;
        }
        
        setSearching(true);

        const filters = { search:searchValue, area, type, quality }

        countQuestions(filters).then((newCount) => {
            
            fetchRecentQuestions(filters).then((newQuestions) => {
                setCount( newCount );
                setQuestions( newQuestions );
                setShowMore( newQuestions.length ==20 );
            })
            .catch(error => {
                setError('Error fetching questions. Please try again later.');
                console.error('Error searching questions:', error);
            })
            .finally(() => {
                setSearching(false);
            });
        })
        .catch((error) => {
            console.error('Error counting questions:', error);
            setError('Error fetching questions. Please try again later.');
            setSearching(false);
        });

    }, [searchValue,area,type,quality]);

    const doShowMore = useCallback(() => {

        const before = questions && questions.length > 0 ? questions[questions.length - 1].timestamp : null;

        setError(null);
        setSearching(true);
        try {
            fetchRecentQuestions( { search:searchValue, area, type, quality, before }).then((data) => {
                setQuestions([...questions, ...data]);
                setShowMore( data.length ==20 );
            })
            .catch(error => {
                console.error('Error fetching more questions:', error);
                setError('Error fetching more questions. Please try again later.');
            })  
            .finally(() => {
                setSearching(false);
            });
        } catch (error) {
            setError('Error fetching more questions. Please try again later.');
            setSearching(false);
        }

    }, [searchValue, area, type, quality, questions]);



    // Ref for the input element
    const inputRef = useRef(null);

    // Autofocus the input on mount
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const showSearch = props.filterSearch;
    const showAreas = false;
    const showTypes = false;
    const showQuality = true;
    const showDate = true;

    return <div className="ask-content container">
            <div className="ask-search pt-5">

                { showSearch && <form className="ask-search-form my-5">
                    <div className="d-flex justify-content-center">
                        <div className="input-group input-group-lg d-flex-sm-column flex-column flex-md-row" style={{maxWidth: '900px'}}>
                            <input 
                                type="text" 
                                className="form-control w-sm-100 w-md-auto" 
                                value={value} 
                                placeholder="Search questions" 
                                ref={inputRef}
                                onChange={e => { setValue(e.target.value); }} 
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        setSearchvalue(value);
                                    }
                                }}
                            />
                            <button 
                                className="btn btn-primary asktrip-search-btn px-4 ask-gradient border-0 w-sm-100 w-md-auto mt-2 mt-md-0" 
                                type="button" 
                                disabled={searching || value.trim().length === 0}
                                onClick={() => {
                                    setSearchvalue(value);
                                    setTimeout(() => {
                                        if (inputRef.current) {
                                            const inputRect = inputRef.current.getBoundingClientRect();
                                            const targetPosition = window.scrollY + inputRect.top - (window.innerHeight * 0.1);
                                            window.scrollTo({
                                                top: targetPosition,
                                                behavior: 'smooth'
                                            });
                                        }
                                    }, 100);
                                }}>
                                Search <img src="/inline-asktrip_logo_white.svg" alt="AskTrip logo" />
                            </button>
                        </div>
                    </div>
                </form> }

                <form className="container d-flex flex-column flex-md-row align-items-center justify-content-center w-100 gap-3 mb-5">
                    <span className='me-3'>Filter&nbsp;questions:</span>

                    { props.filterAreas && <select className="form-select" value={area ?? ""} onChange={(e) => {
                        const value = e.target.value;
                        setArea(value === "" ? null : value);
                    }}>
                        <option value="">Clinical area</option>
                        {areas && areas.map((a, i) => (
                            <option key={a.title} value={a.title}>{a.title}</option>
                        ))}
                    </select> }

                    { props.filterTypes && <select className="form-select" value={type ?? ""} onChange={(e) => {
                        const value = e.target.value;
                        setType(value === "" ? null : value);
                    }}>
                        <option value="">Question type</option>
                        {types && types.map((t, i) => (
                            <option key={t.title} value={t.title}>{t.title}</option>
                        ))}
                    </select> }

                    { props.filterQuality && <select className="form-select" value={quality ?? ""} onChange={(e) => {
                        const value = e.target.value;
                        setQuality(value === "" ? null : value);
                    }}>
                        <option value="">Quality of Evidence</option>
                        {qualities && qualities.map((q, i) => (
                            <option key={q} value={q}>{q}</option>
                        ))}
                    </select> }
                </form>

                {searching && <div className="d-flex w-100 justify-content-center align-items-center p-3"><LoadingAnimation /></div>}

                {error && <div className="alert alert-danger"> {error} </div>}
                
                <div className='d-flex justify-content-center mt-5 mb-3'>
                    {count !== null && count !==0 && <p className="text-body text-muted">{count} matching question{count!==1 && 's'}</p> }
                    {count === 0 && !searching && <p className="text-body text-muted">No matching questions</p>}
                </div>

                <div className="ask-questions-grid">

                    {questions && questions.length > 0 && questions.map((q, i) => (
                        <div key={q.questionId} className="bg-white p-3 border rounded-3 text-start">
                            <a href={`/ask/${q.slug}-${q.questionId}`} className="text-decoration-none fs-5 fw-bold truncate-4-lines">{q.question}</a>
                            { showAreas && <div className="ask-areas">
                                <h4 className="fs-6 text-muted mb-1 mt-3">Clinical area</h4>
                                <ul className="list-unstyled d-inline fw-bold m-0 p-0">
                                    {q.areas?.map((a, i) => (
                                        <li key={a} className="d-inline">{a}{i < q.areas.length - 1 && ', '}</li>
                                    ))}
                                </ul>
                            </div> }

                            { showTypes && <div className="ask-types">
                                <h4 className="fs-6 text-muted mb-1 mt-3">Question type</h4>
                                <ul className="list-unstyled d-inline fw-bold m-0 p-0">
                                    {q.types?.map((a, i) => (
                                        <li key={a} className="d-inline">{a}{i < q.types.length - 1 && ', '}</li>
                                    ))}
                                </ul>
                            </div> }

                            { showDate && <div className="ask-date">
                                <h4 className="fs-6 text-muted mb-1 mt-3">Question asked</h4>
                                <ul className="list-unstyled d-inline fw-bold m-0 p-0">
                                    <li className="d-inline">{formatDate(q.timestamp)}</li>
                                </ul>
                            </div> }

                            { showQuality && <div className="ask-quality">
                                <h4 className="fs-6 text-muted mb-1 mt-3">Quality of evidence</h4>
                                <ul className="list-unstyled d-inline fw-bold m-0 p-0">
                                    <li className="d-inline">{q.quality || 'n/a'}</li>
                                </ul>
                            </div> }

                        </div>
                    ))}
                    {!searching && showMore  && <button className="btn btn-primary asktrip-search-btn px-4 ask-gradient border-0 w-100" onClick={() => doShowMore() }>More ...</button> }
                </div>
            </div>
        </div>

}
